import React, { FC, useState } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';

import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { BaseStyleEnums } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import { Ratio, Size } from '~/core/forge/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { createForgeImageURL, getEnvironmentAwareHref } from '~/core/utils';
import ShareBar from '~/views/components/shareBar/ShareBar';
import VideoPlayer from '~/views/components/videoPlayer/VideoPlayer';

import { useGameRecapDetailsQuery, useGameRecapQuery } from '../../api';
import { LOADING_DISPLAY_SIZE } from '../../styles';

import {
    ArticleInformation,
    Author,
    AuthorAndDate,
    AuthorAndLinksContainer,
    AuthorContainer,
    GameRecapContainer,
    IconContainer,
    ImageCaption,
    LoadingRecapContainer,
    RecapImage,
    RecapImageContainer,
    RecapLinkedButton,
    SecondaryTextContainer,
    Summary,
    VideoContainer,
} from './styles';

type Props = {
    gameId?: number;
};

const GameRecap: FC<Props> = ({ gameId }) => {
    const intl = useIntl();
    const screenSize = useSizeWithThrottle();
    const isWebview = useIsWebview();

    const isLarge = screenSize.width > BaseStyleEnums.ScreenWidthBreakpoints.large;

    const { data: recapSummary, isLoading: isSummaryLoading } = useGameRecapQuery(gameId);
    const summaryItem = recapSummary?.items?.[0];

    const { data: recapDetails } = useGameRecapDetailsQuery(summaryItem?.selfUrl);
    const detailItem = recapDetails?.parts?.[0];

    const recapClip = useOptionForLocale<string>({
        [window.__SITE_SETTINGS__.languageCode]:
            detailItem && detailItem.fields?.brightcoveId && detailItem.fields.brightcoveId,
    });

    const [videoOpen, setVideoOpen] = useState(false);

    if (isSummaryLoading) {
        return (
            <LoadingRecapContainer>
                <LoadingDisplay isLoading isInline size={LOADING_DISPLAY_SIZE} />
            </LoadingRecapContainer>
        );
    }

    let templateUrl: string | undefined = undefined;

    if (detailItem?.thumbnail?.templateUrl) {
        templateUrl = detailItem?.thumbnail?.templateUrl;
    } else if (summaryItem?.thumbnail?.templateUrl) {
        templateUrl = summaryItem?.thumbnail?.templateUrl;
    }

    const imageUrl = templateUrl ? createForgeImageURL(templateUrl, Ratio.LANDSCAPE_16_TO_9, Size.SIZE_40) : '';

    return summaryItem ? (
        <GameRecapContainer>
            {recapDetails && (
                <ArticleInformation>
                    <h2>
                        <ReactMarkdown>{summaryItem.headline}</ReactMarkdown>
                    </h2>
                    <SecondaryTextContainer>{summaryItem.fields.description}</SecondaryTextContainer>
                </ArticleInformation>
            )}
            {imageUrl ? (
                <VideoContainer
                    $isClickable={recapClip !== undefined}
                    onClick={() => !videoOpen && recapClip && setVideoOpen(true)}
                >
                    {videoOpen && recapClip && <VideoPlayer videoId={recapClip} isInline />}
                    {!videoOpen && (
                        <RecapImageContainer $isHidden={videoOpen && isLarge}>
                            <RecapImage src={imageUrl} alt="" />
                            {recapClip && (
                                <IconContainer>
                                    <PlayCircleOutlineIcon />
                                </IconContainer>
                            )}
                        </RecapImageContainer>
                    )}
                </VideoContainer>
            ) : (
                <LoadingRecapContainer>
                    <LoadingDisplay isLoading isInline size={LOADING_DISPLAY_SIZE} />
                </LoadingRecapContainer>
            )}
            <ImageCaption>{detailItem && detailItem.fields?.description}</ImageCaption>
            <AuthorAndLinksContainer>
                <AuthorAndDate>
                    <AuthorContainer>
                        <FormattedMessage
                            description="A message displaying the author of an article"
                            defaultMessage="By<b>{author}</b>"
                            values={{
                                b: (text) => <Author>{text}</Author>,
                                author:
                                    recapDetails.references?.contributor?.length > 0
                                        ? recapDetails.references.contributor[0]?.title
                                        : summaryItem.fields.contributorOverride,
                            }}
                        />
                        {recapDetails.references?.contributor?.length > 0 &&
                            recapDetails.references.contributor[0]?.fields.source && (
                                <FormattedMessage
                                    description="A message displaying the source of an article"
                                    defaultMessage="<b>/ {source}</b>"
                                    values={{
                                        b: (text) => <Author>{text}</Author>,
                                        source: recapDetails.references.contributor[0].fields.source,
                                    }}
                                />
                            )}
                    </AuthorContainer>
                    <SecondaryTextContainer>
                        {DateTime.fromISO(recapDetails.lastUpdatedDate, {
                            locale: intl.locale,
                        }).toLocaleString(DateTime.DATE_FULL)}
                    </SecondaryTextContainer>
                </AuthorAndDate>
                <ShareBar
                    linkBase={`https://www.nhl.com${getEnvironmentAwareHref('/news/', isWebview)}`}
                    slug={recapDetails.slug}
                    title={summaryItem.headline}
                />
            </AuthorAndLinksContainer>
            <Summary>
                <ReactMarkdown>{summaryItem.summary}</ReactMarkdown>
            </Summary>
            <RecapLinkedButton type="link" href={getEnvironmentAwareHref(`/news/${summaryItem.slug}`, isWebview)}>
                <FormattedMessage
                    description="A phrase describing there is full coverage for the game."
                    defaultMessage="Full Coverage"
                />
            </RecapLinkedButton>
        </GameRecapContainer>
    ) : null;
};

export default GameRecap;
